import { getLocaleStringWithPrecision } from "@shared/utils/numbers";
import React, { useContext, useMemo, useState } from "react";
import styled, { useTheme } from "styled-components";
import moment from "moment";
import { TradeDetailsContext } from "@shared/features/trades/TradeDetails.context";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  LanguageNameSpaces,
  OrderCardData,
  OrderStatus,
  OrderTypes,
  PrecisionType,
} from "@shared/types";
import {
  DISPLAY_CURRENCIES,
  DOLLAR_COINS,
  MARGIN_EXCHANGES,
  ENTRY_ACTIVITY_IDS,
  STOP_ACTIVITY_IDS,
  TAKE_PROFIT_ACTIVITY_IDS,
} from "@shared/common/constants";
import { getAnnotationColor } from "@shared/features/trades/common/utils";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import TextWithPrecision from "common/Text/TextWithPrecision";
import NewLookTimelineRow, {
  Item,
  Rail,
  RailStyle,
  TimelineRowData,
} from "features/trades/TradeDrawer/Timeline/NewLookTimelineRow.tsx";
import { formatDateLabel } from "@shared/utils/date";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";

type Entry =
  | { kind: "label"; key: string; text: string; pending: boolean }
  | { kind: "row"; row: TimelineRowData; pending: boolean };

const TITLE_RE = /^(.*?)\s*(\d+)\s*\/\s*(\d+)$/;

const getType = (component: number): OrderTypes | undefined =>
  ENTRY_ACTIVITY_IDS.includes(component)
    ? OrderTypes.ENTRY
    : TAKE_PROFIT_ACTIVITY_IDS.includes(component)
    ? OrderTypes.TAKE_PROFIT
    : STOP_ACTIVITY_IDS.includes(component)
    ? OrderTypes.STOP
    : undefined;

/** The rail style leaving item `i` downwards. */
const railBelow = (entries: Entry[], i: number): RailStyle => {
  const entry = entries[i];
  if (!entry) return "none";
  if (entry.kind === "label") return railBelow(entries, i - 1);
  return entry.pending ? "dashed" : "solid";
};

/**
 * New-look "Active Trade Timeline": a progress header (entries filled, average
 * entry), then the events newest first on a rail. Orders still ahead sit on
 * top with hollow dots and a dashed rail; filled events are solid, grouped by
 * day.
 */
const NewLookTimeline = () => {
  const theme = useTheme();
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const {
    activities,
    ordersData,
    statsData,
    pricePrecision,
    amountPrecision,
    isGridBotTrade,
  } = useContext(TradeDetailsContext)!;
  const tokens = getNewLookTokens(theme);
  // Prices are in the quote coin; order amounts are in the trade's main coin
  // (the base coin on inverse pairs), so the two get their own units.
  const coin = ordersData?.data?.price_coin ?? statsData?.main_coin;
  const isDollar = !!coin && DOLLAR_COINS.includes(coin);
  const amountCoin = ordersData?.data?.amount_coin ?? statsData?.main_coin;
  const exchange = ordersData?.data?.exchange;
  const isMarginExchange = !!exchange && MARGIN_EXCHANGES.has(exchange);
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  // Dollar amounts read in cents, whatever the symbol's amount precision.
  const formatAmount = (amount: number) =>
    amountCoin && DOLLAR_COINS.includes(amountCoin)
      ? `$${getLocaleStringWithPrecision(
          amount,
          getDisplayCurrencyAmountPrecision(DISPLAY_CURRENCIES.USD)
        )}`
      : `${getLocaleStringWithPrecision(amount, amountPrecision)} ${
          amountCoin ?? ""
        }`.trim();

  const renderPrice = (price?: number) =>
    price ? (
      <>
        {isDollar && "$"}
        <TextWithPrecision
          text={price}
          precisionType={PrecisionType.PRICE}
          precisionCoin={coin}
          precision={pricePrecision}
        />
      </>
    ) : undefined;

  const entries = useMemo(() => {
    const typeColor = (type?: OrderTypes) =>
      type ? getAnnotationColor(type, theme) : theme.colors.primaryBlue;
    const cardsOf = (type: OrderTypes): OrderCardData[] =>
      (type === OrderTypes.ENTRY
        ? ordersData?.data?.entry_order_cards
        : type === OrderTypes.TAKE_PROFIT
        ? ordersData?.data?.take_profit_order_cards
        : ordersData?.data?.stop_order_cards) ?? [];

    // Filled events, newest first.
    const events = [...(activities ?? [])].sort(
      (a, b) => b.relevant_time - a.relevant_time
    );
    const dayKey = (time: number) => moment.unix(time).format("YYYY-MM-DD");
    const days = new Set(events.map((e) => dayKey(e.relevant_time)));

    // Orders that have not happened yet (not meaningful for grid bots).
    const pending: TimelineRowData[] = [];
    if (!isGridBotTrade && ordersData) {
      const toRow = (type: OrderTypes, card: OrderCardData, count: number) => {
        const label =
          type === OrderTypes.ENTRY
            ? t("timeline.entry")
            : type === OrderTypes.TAKE_PROFIT
            ? t("timeline.take_profit")
            : t("timeline.stop");
        pending.push({
          key: `pending-${type}-${card.id ?? card.number}`,
          title: type === OrderTypes.STOP ? label : `${label} ${card.number}`,
          total: type === OrderTypes.STOP ? undefined : `/${count}`,
          meta: t("timeline.pending"),
          price: renderPrice(card.target_price),
          color: typeColor(type),
          pending: true,
        });
      };
      const isPending = (c: OrderCardData) => c.status === OrderStatus.PENDING;
      // The next pending order of each kind, highest price first, so the list
      // reads top-down like the chart (long: take-profit, entry, stop; short:
      // the mirror).
      [OrderTypes.TAKE_PROFIT, OrderTypes.ENTRY, OrderTypes.STOP]
        .flatMap((type) => {
          const cards = cardsOf(type);
          const next = cards
            .filter(isPending)
            .sort((a, b) => (a.number ?? 0) - (b.number ?? 0))[0];
          return next ? [{ type, card: next, count: cards.length }] : [];
        })
        .sort(
          (a, b) =>
            (Number(b.card.target_price) || 0) -
            (Number(a.card.target_price) || 0)
        )
        .forEach(({ type, card, count }) => toRow(type, card, count));
    }

    const showLabels = days.size > 1 || pending.length > 0;
    const now = moment();
    const dayLabel = (time: number) => {
      const m = moment.unix(time);
      if (m.isSame(now, "day")) return t("timeline.today");
      if (m.isSame(now.clone().subtract(1, "day"), "day"))
        return t("timeline.yesterday");
      return formatDateLabel(m, "MMM D");
    };

    // An order card only carries its current filled amount, so it goes on the
    // order's most recent event alone (events are newest first); an earlier
    // partial fill would otherwise show the later, larger amount.
    const amountShown = new Set<string>();
    const filledRows = events.map((a): [TimelineRowData, number] => {
      const type = getType(a.activity_component);
      const m = moment.unix(a.relevant_time).locale("en");
      const match = TITLE_RE.exec(a.description);
      const card =
        match && type
          ? cardsOf(type).find((c) => c.number === Number(match[2]))
          : undefined;
      const parts = [
        showLabels
          ? m.format("HH:mm")
          : `${m.format("HH:mm")} · ${formatDateLabel(m, "MMM D")}`,
      ];
      const cardKey = card && `${type}-${card.number}`;
      if (card?.filled_amount && cardKey && !amountShown.has(cardKey)) {
        amountShown.add(cardKey);
        parts.push(
          // Futures order sizes are the margin committed (position size /
          // leverage, plus a fee buffer), so they say so; spot is the amount.
          isMarginExchange
            ? t("timeline.margin_amount", {
                amount: formatAmount(card.filled_amount),
              })
            : t("timeline.filled_amount", {
                amount: formatAmount(card.filled_amount),
              })
        );
      }
      if (match && a.status && a.status !== OrderStatus.FULFILLED)
        parts.push(a.status);
      return [
        {
          key: `${a.relevant_time}-${a.activity_component}-${a.description}`,
          title: match
            ? `${match[1].trim()} ${match[2]}`
            : a.status
            ? `${a.description} ${a.status.toLowerCase()}`
            : a.description,
          total: match ? `/${match[3]}` : undefined,
          meta: parts.join(" · "),
          price: renderPrice(a.execution_price),
          color: typeColor(type),
          pending: false,
        },
        a.relevant_time,
      ];
    });

    // Flatten into labels + rows, then derive rail styles. The rail below an
    // item is dashed while that item is still ahead, solid otherwise.
    const flat: Entry[] = [];
    if (pending.length) {
      if (showLabels)
        flat.push({
          kind: "label",
          key: "l-up",
          text: t("timeline.upcoming"),
          pending: true,
        });
      pending.forEach((row) => flat.push({ kind: "row", row, pending: true }));
    }
    let lastDay = "";
    filledRows.forEach(([row, time]) => {
      const d = dayKey(time);
      if (showLabels && d !== lastDay) {
        flat.push({
          kind: "label",
          key: `l-${d}`,
          text: dayLabel(time),
          pending: false,
        });
        lastDay = d;
      }
      flat.push({ kind: "row", row, pending: false });
    });
    return flat;
  }, [
    activities,
    ordersData,
    statsData,
    isGridBotTrade,
    theme,
    t,
    coin,
    pricePrecision,
    amountPrecision,
  ]);

  // Long histories (a grid fills dozens of levels) show the latest few
  // events; the rest is one tap away.
  const [expanded, setExpanded] = useState(false);
  const { shown, hiddenCount } = useMemo(() => {
    const filledIndexes = entries
      .map((entry, i) => (entry.kind === "row" && !entry.pending ? i : -1))
      .filter((i) => i >= 0);
    if (expanded || filledIndexes.length <= COLLAPSED_FILLED + 2)
      return { shown: entries, hiddenCount: 0 };
    const cut = filledIndexes[COLLAPSED_FILLED - 1] + 1;
    return {
      shown: entries.slice(0, cut),
      hiddenCount: filledIndexes.length - COLLAPSED_FILLED,
    };
  }, [entries, expanded]);

  const total = statsData?.entries_filled?.total;
  const filledCount = statsData?.entries_filled?.number ?? 0;
  const showHeader = !isGridBotTrade && !!total;

  return (
    <Wrapper>
      {showHeader && (
        <Summary>
          <SummaryTop>
            <Headline>
              {filledCount === 1
                ? t("timeline.entries_filled_one", {
                    count: filledCount,
                    total,
                  })
                : t("timeline.entries_filled_other", {
                    count: filledCount,
                    total,
                  })}
            </Headline>
            {(statsData?.take_profits_filled ?? 0) > 0 && (
              <SummaryMeta>
                {statsData!.take_profits_filled === 1
                  ? t("timeline.take_profits_filled_one", { count: 1 })
                  : t("timeline.take_profits_filled_other", {
                      count: statsData!.take_profits_filled,
                    })}
              </SummaryMeta>
            )}
          </SummaryTop>
          <Segments aria-hidden>
            {Array.from({ length: total }, (_, i) => (
              <Segment
                key={i}
                style={{
                  background:
                    i < filledCount ? tokens.up : tokens.hairlineStrong,
                }}
              />
            ))}
          </Segments>
          {!!statsData?.entry_price && (
            <SummaryMeta>
              {t("timeline.avg_entry")}{" "}
              <Strong>{renderPrice(statsData.entry_price)}</Strong>
              {coin && !isDollar && ` ${coin}`}
            </SummaryMeta>
          )}
        </Summary>
      )}
      <List>
        {shown.map((entry, i) => {
          const prev = shown[i - 1];
          // A label carries the rail of whatever sits above it (none when
          // first); a row's lower half is dashed while it is still ahead.
          const prevRail = railBelow(shown, i - 1);
          const rail = railBelow(shown, i);
          const isLast = i === shown.length - 1 && !hiddenCount;
          if (entry.kind === "label") {
            return (
              <Item key={entry.key}>
                <Rail $top={prevRail} $bottom={prevRail} />
                <GroupLabel>{entry.text}</GroupLabel>
              </Item>
            );
          }
          return (
            <NewLookTimelineRow
              key={entry.row.key}
              row={entry.row}
              top={prev ? prevRail : "none"}
              bottom={isLast ? "none" : rail}
            />
          );
        })}
      </List>
      {hiddenCount > 0 && (
        <ShowAll type="button" onClick={() => setExpanded(true)}>
          {t("timeline.show_all", { count: hiddenCount })}
        </ShowAll>
      )}
    </Wrapper>
  );
};

export default NewLookTimeline;

const COLLAPSED_FILLED = 6;

const ShowAll = styled.button`
  display: block;
  margin: 4px 0 0 28px;
  padding: 6px 0;
  border: 0;
  background: none;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  color: ${(p) => getNewLookTokens(p.theme).accent};
  &:hover {
    opacity: 0.8;
  }
`;
const Wrapper = styled.div`
  width: 100%;
  padding: 4px 0 24px;
`;
const Summary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  margin-bottom: 14px;
  border-radius: 12px;
  border: ${(p) => p.theme.borders.panel};
  box-shadow: ${(p) => p.theme.shadows.panel};
  background: ${(p) => getNewLookTokens(p.theme).well};
`;
const SummaryTop = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;
const Headline = styled.span`
  ${newLookType.figure};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const SummaryMeta = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Strong = styled.span`
  color: ${(p) => getNewLookTokens(p.theme).ink};
  font-weight: 500;
`;
const Segments = styled.div`
  display: flex;
  gap: 4px;
`;
const Segment = styled.span`
  flex: 1;
  height: 4px;
  border-radius: 2px;
`;
const List = styled.div`
  display: flex;
  flex-direction: column;
`;
const GroupLabel = styled.div`
  ${newLookType.micro};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  padding: 14px 0 0;
`;
